import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone';

import {
  ModalOverlay,
  ResponsiveContent,
  ModalCloseHeader
} from '@evdy/web-core/dist/components/shared/Modal2020';
import { formatPhoneNumber } from '@evdy/web-core/dist/lib/utils';

import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';
import { webCommunityOperations } from '@evdy/web-redux/dist/lib/webCommunity';

import TextInput from '../TextInput';

import styles from './LoginModal.module.scss';

const { sendPin } = authUserOperations;
const { verifyPinStepForm, verifyPinCreateUser } = webCommunityOperations;

const {
  'login-modal': loginModalClass,
  'button-container': buttonContainerClass,
  title: titleClass,
  subtitle: subtitleClass,
  inputs: inputsClass
} = styles;

const LoginModal = ({ closeModal, baseRoute }) => {
  const { authedUser } = useSelector(({ authUser }) => ({
    authedUser: authUser.data
  }));
  const { _id: userId, name } = authedUser || {};
  const { first: userFirstName, last: userLastName, middle: userMiddleName } = name || {};
  const isLoggedIn = !!userId;
  const isNewUser =
    isLoggedIn && [userFirstName, userMiddleName, userLastName].filter(Boolean).length < 1;
  // form values and submit fn's
  let titleCopy = '';
  let subtitleCopy = '';
  let initialFormValues = {};
  let validationSchema = {};
  let submitFn = () => {};
  let formBody = {};

  const dispatch = useDispatch();

  const [currentPhone, setCurrentPhone] = useState(null);
  const [currentPin, setCurrentPin] = useState(null);
  const [currentForm, setCurrentForm] = useState('phone');

  // if successfully logged in && !newUser close modal && reset phone state
  useEffect(() => {
    if (!isNewUser && isLoggedIn) {
      setCurrentPhone(null);
      setCurrentPin(null);
      browserHistory.push(baseRoute);
    }
  }, [baseRoute, isLoggedIn, isNewUser]);

  const EnterPhoneFormSchema = Yup.object().shape({
    phone: Yup.string().phone('US', true, 'Please enter a valid phone number.')
  });

  const EnterPhoneForm = () => (
    <Form>
      <div className={inputsClass}>
        <TextInput type="tel" label="Phone" name="phone" placeholder="e.g. (123) 555-1212" />
      </div>
      <div className={buttonContainerClass}>
        <button type="submit">Next</button>
      </div>
    </Form>
  );

  const EnterPinFormSchema = Yup.object().shape({
    pin: Yup.string()
      .min(4)
      .max(4)
      .required('Pin is required!')
  });

  const EnterPinForm = () => (
    <Form>
      <div className={inputsClass}>
        <TextInput type="text" pattern="[0-9]*" label="" name="pin" placeholder="ex. 1234" />
      </div>
      <div className={buttonContainerClass}>
        <button type="submit">Next</button>
      </div>
    </Form>
  );

  const emailRegex = /^\S+@\S+\.\S+$/;

  const NameAndEmailFormSchema = Yup.object().shape({
    name: Yup.string().required('This is required!'),
    email: Yup.string().matches(emailRegex, 'Please provide a valid email')
  });

  const EnterNameAndEmailForm = () => (
    <Form>
      <div className={inputsClass}>
        <TextInput type="text" label="Full Name (required)" name="name" placeholder="John Smith" />
        <TextInput type="email" label="Email" name="email" placeholder="johnsmith@email.com" />
      </div>
      <div className={buttonContainerClass}>
        <button type="submit">Finish</button>
      </div>
    </Form>
  );

  //assign variable and fn values depending on form step
  switch (true) {
    case currentForm === 'pin' && !!currentPhone && !isLoggedIn:
      titleCopy = `Enter the 4 digit code we sent to you at ${formatPhoneNumber(
        currentPhone
      )} via text message`;
      subtitleCopy = '';
      initialFormValues = { pin: '' };
      validationSchema = EnterPinFormSchema;
      submitFn = submitArgs => {
        const stepFormFn = () => setCurrentForm('name_email');
        setCurrentPin(submitArgs.pin);
        dispatch(
          verifyPinStepForm({
            stepFormFn,
            phone: currentPhone,
            pin: submitArgs.pin
          })
        );
      };
      formBody = <EnterPinForm />;
      break;
    case currentForm === 'name_email':
      titleCopy = 'Looks like this is your first time using Everdays';
      subtitleCopy = 'Let us know who you are';
      initialFormValues = { name: '', email: '' };
      validationSchema = NameAndEmailFormSchema;
      submitFn = submitArgs => {
        //split name on space and handle middle and last if applicable
        const nameArray = submitArgs.name.split(' ');
        const name =
          nameArray.length > 2
            ? {
                first: !!nameArray[0] ? nameArray[0] : '',
                middle: !!nameArray[1] ? nameArray[1] : '',
                last: !!nameArray[2] ? nameArray[2] : ''
              }
            : nameArray.length === 2
            ? {
                first: !!nameArray[0] ? nameArray[0] : '',
                last: !!nameArray[1] ? nameArray[1] : ''
              }
            : nameArray.length === 1
            ? { first: !!nameArray[0] ? nameArray[0] : '' }
            : {};
        dispatch(
          verifyPinCreateUser({
            name,
            email: submitArgs.email,
            phone: currentPhone,
            pin: currentPin
          })
        );
      };
      formBody = <EnterNameAndEmailForm />;
      break;
    case currentForm === 'phone' && !isLoggedIn:
      titleCopy = 'Please enter your phone number';
      subtitleCopy = "We'll send you a text message to confirm your details.";
      initialFormValues = { phone: '' };
      validationSchema = EnterPhoneFormSchema;
      submitFn = submitArgs => {
        const strippedPhone = submitArgs?.phone?.replace(/\D+/g, '');
        setCurrentPhone(strippedPhone);
        dispatch(sendPin({ ...submitArgs, platform: 'web' }));
        setCurrentForm('pin');
      };
      formBody = <EnterPhoneForm />;
      break;
    default:
      return null;
  }

  return (
    <ModalOverlay modalVisible {...{ closeModal }} customClass={loginModalClass}>
      <ResponsiveContent>
        <ModalCloseHeader {...{ closeModal }} />
        {!!titleCopy && <h3 className={titleClass}>{titleCopy}</h3>}
        {!!subtitleCopy && <p className={subtitleClass}>{subtitleCopy}</p>}
        <Formik
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={submitFn}
        >
          {formBody}
        </Formik>
      </ResponsiveContent>
    </ModalOverlay>
  );
};

export default LoginModal;
